import React from 'react';
import { JobSource, FormErrors } from '../types';
import ArrayFieldEditor from '../fields/ArrayFieldEditor';
import StructuredFieldEditor from '../fields/StructuredFieldEditor';
import FAQEditor from '../fields/FAQEditor';

interface AgentInfoSectionProps {
  formData: JobSource;
  setFormData: React.Dispatch<React.SetStateAction<JobSource>>;
  errors: FormErrors;
}

const AgentInfoSection: React.FC<AgentInfoSectionProps> = ({
  formData,
  setFormData,
  errors,
}) => {
  const handleChange = (field: keyof JobSource, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field: keyof JobSource, value: string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleStructuredArrayChange = (
    field: 'benefits' | 'merits' | 'demerits' | 'flow',
    value: Array<{ heading: string; content: string }>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFAQChange = (value: Array<{ question: string; answer: string }>) => {
    setFormData((prev) => ({ ...prev, faqs: value }));
  };

  return (
    <div className="tab-pane active">
      <div className="form-group mb-3">
        <label htmlFor="contract_type_name" className="form-label fw-semibold">
          契約形態名（テキスト）
        </label>
        <input
          type="text"
          id="contract_type_name"
          className={`form-control ${errors.contract_type_name ? 'is-invalid' : ''}`}
          value={formData.contract_type_name || ''}
          onChange={(e) => handleChange('contract_type_name', e.target.value)}
        />
        <small className="form-text text-muted">上記のリレーションとは別に、テキストで契約形態を記載する場合に使用（後方互換性のため残しています）</small>
        {errors.contract_type_name && (
          <div className="invalid-feedback">{errors.contract_type_name.join(', ')}</div>
        )}
      </div>

      <div className="form-group mb-3">
        <label htmlFor="payment_info" className="form-label fw-semibold">
          支払い情報
        </label>
        <textarea
          id="payment_info"
          className={`form-control ${errors.payment_info ? 'is-invalid' : ''}`}
          value={formData.payment_info || ''}
          onChange={(e) => handleChange('payment_info', e.target.value)}
          placeholder="例: 月末締め翌月末払い、支払いサイト30日"
          rows={3}
        />
        {errors.payment_info && (
          <div className="invalid-feedback">{errors.payment_info.join(', ')}</div>
        )}
      </div>

      <div className="form-group mb-3">
        <label htmlFor="introduction_record" className="form-label fw-semibold">
          紹介実績
        </label>
        <textarea
          id="introduction_record"
          className={`form-control ${errors.introduction_record ? 'is-invalid' : ''}`}
          value={formData.introduction_record || ''}
          onChange={(e) => handleChange('introduction_record', e.target.value)}
          placeholder="例: 株式会社A、株式会社B、株式会社C"
          rows={3}
        />
        {errors.introduction_record && (
          <div className="invalid-feedback">{errors.introduction_record.join(', ')}</div>
        )}
      </div>

      <div className="form-group mb-3">
        <label htmlFor="description" className="form-label fw-semibold">
          説明
        </label>
        <textarea
          id="description"
          className={`form-control ${errors.description ? 'is-invalid' : ''}`}
          value={formData.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="例: 500文字程度でそのエージェントもしくは求人サイトの説明文を記載してください"
          rows={5}
        />
        {errors.description && (
          <div className="invalid-feedback">{errors.description.join(', ')}</div>
        )}
      </div>

      <div className="form-group mb-3">
        <label htmlFor="catch_phrase" className="form-label fw-semibold">
          キャッチフレーズ
        </label>
        <textarea
          id="catch_phrase"
          className={`form-control ${errors.catch_phrase ? 'is-invalid' : ''}`}
          value={formData.catch_phrase || ''}
          onChange={(e) => handleChange('catch_phrase', e.target.value)}
          placeholder="例: そのエージェントを一言で魅力を伝えるメッセージを30文字以内で記載してください"
          rows={3}
        />
        {errors.catch_phrase && (
          <div className="invalid-feedback">{errors.catch_phrase.join(', ')}</div>
        )}
      </div>

      <div className="form-group mb-3">
        <label htmlFor="review_article_url" className="form-label fw-semibold">
          レビュー記事URL
        </label>
        <input
          type="url"
          id="review_article_url"
          className={`form-control ${errors.review_article_url ? 'is-invalid' : ''}`}
          value={formData.review_article_url || ''}
          onChange={(e) => handleChange('review_article_url', e.target.value)}
          placeholder="例: インディバースフリーランスや各種メディアで書かれた評判記事のURLを指定してください"
        />
        {errors.review_article_url && (
          <div className="invalid-feedback">{errors.review_article_url.join(', ')}</div>
        )}
      </div>

      <div className="row">
        <div className="col-md-6">
          <div className="form-group mb-3">
            <label htmlFor="public_job_listings_count" className="form-label fw-semibold">
              公開求人数
            </label>
            <input
              type="number"
              id="public_job_listings_count"
              className={`form-control ${errors.public_job_listings_count ? 'is-invalid' : ''}`}
              value={formData.public_job_listings_count || ''}
              onChange={(e) => handleChange('public_job_listings_count', parseInt(e.target.value, 10) || 0)}
            />
            <small className="form-text text-muted">手動入力</small>
            {errors.public_job_listings_count && (
              <div className="invalid-feedback">{errors.public_job_listings_count.join(', ')}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-group mb-3">
            <label htmlFor="remote_job_listings_count" className="form-label fw-semibold">
              リモート求人数
            </label>
            <input
              type="number"
              id="remote_job_listings_count"
              className={`form-control ${errors.remote_job_listings_count ? 'is-invalid' : ''}`}
              value={formData.remote_job_listings_count || ''}
              onChange={(e) => handleChange('remote_job_listings_count', parseInt(e.target.value, 10) || 0)}
            />
            <small className="form-text text-muted">手動入力</small>
            {errors.remote_job_listings_count && (
              <div className="invalid-feedback">{errors.remote_job_listings_count.join(', ')}</div>
            )}
          </div>
        </div>
      </div>

      <div className="form-group mb-3">
        <label htmlFor="margin" className="form-label fw-semibold">
          マージン
        </label>
        <textarea
          id="margin"
          className={`form-control ${errors.margin ? 'is-invalid' : ''}`}
          value={formData.margin || ''}
          onChange={(e) => handleChange('margin', e.target.value)}
          rows={3}
        />
        {errors.margin && (
          <div className="invalid-feedback">{errors.margin.join(', ')}</div>
        )}
      </div>

      <div className="form-group mb-3">
        <label htmlFor="sidework" className="form-label fw-semibold">
          副業
        </label>
        <textarea
          id="sidework"
          className={`form-control ${errors.sidework ? 'is-invalid' : ''}`}
          value={formData.sidework || ''}
          onChange={(e) => handleChange('sidework', e.target.value)}
          rows={3}
        />
        {errors.sidework && (
          <div className="invalid-feedback">{errors.sidework.join(', ')}</div>
        )}
      </div>

      <hr className="my-4" />

      <StructuredFieldEditor
        label="メリット"
        field="merits"
        items={formData.merits || []}
        onChange={(items) => handleStructuredArrayChange('merits', items)}
        errors={errors.merits}
      />

      <StructuredFieldEditor
        label="デメリット"
        field="demerits"
        items={formData.demerits || []}
        onChange={(items) => handleStructuredArrayChange('demerits', items)}
        errors={errors.demerits}
      />

      <StructuredFieldEditor
        label="福利厚生"
        field="benefits"
        items={formData.benefits || []}
        onChange={(items) => handleStructuredArrayChange('benefits', items)}
        errors={errors.benefits}
      />

      <StructuredFieldEditor
        label="フロー"
        field="flow"
        items={formData.flow || []}
        onChange={(items) => handleStructuredArrayChange('flow', items)}
        errors={errors.flow}
      />

      <ArrayFieldEditor
        label="対応職種（テキスト）"
        field="supported_occupations"
        items={formData.supported_occupations || []}
        onChange={(items) => handleArrayChange('supported_occupations', items)}
        errors={errors.supported_occupations}
      />

      <ArrayFieldEditor
        label="非対応職種"
        field="unsupported_occupations"
        items={formData.unsupported_occupations || []}
        onChange={(items) => handleArrayChange('unsupported_occupations', items)}
        errors={errors.unsupported_occupations}
      />

      <ArrayFieldEditor
        label="対応エリア（テキスト）"
        field="supported_areas"
        items={formData.supported_areas || []}
        onChange={(items) => handleArrayChange('supported_areas', items)}
        errors={errors.supported_areas}
      />

      <ArrayFieldEditor
        label="特徴"
        field="supported_features"
        items={formData.supported_features || []}
        onChange={(items) => handleArrayChange('supported_features', items)}
        errors={errors.supported_features}
      />

      <ArrayFieldEditor
        label="向いている人"
        field="suitable_people"
        items={formData.suitable_people || []}
        onChange={(items) => handleArrayChange('suitable_people', items)}
        errors={errors.suitable_people}
      />

      <ArrayFieldEditor
        label="向いていない人"
        field="unsuitable_people"
        items={formData.unsuitable_people || []}
        onChange={(items) => handleArrayChange('unsuitable_people', items)}
        errors={errors.unsuitable_people}
      />

      <FAQEditor
        items={formData.faqs || []}
        onChange={handleFAQChange}
        errors={errors.faqs}
      />
    </div>
  );
};

export default AgentInfoSection;



